const BlockType = require('../../extension-support/block-type');
const ArgumentType = require('../../extension-support/argument-type');
const Scratch3LooksBlocks = require('../../blocks/scratch3_looks');

class Scratch3SpeechRecognitionExtension {
    constructor(runtime) {
        this.runtime = runtime;
        this.recognition = null;
        this.lastResult = '';
        this.noiseThreshold = 30;
        this.autoClear = false;
        this.clearAfterSeconds = null;
        this.clearAfterPause = null;
        this.clearTimer = null;
        this.isListening = false;
        this.language = 'en-US';
    }

    getInfo() {
        return {
            id: 'speechRecognition',
            name: 'Speech Recognition',
            blocks: [
                {
                    opcode: 'recognizeSpeech',
                    blockType: BlockType.COMMAND,
                    text: 'recognize speech for [SECONDS] s in [LANGUAGE]',
                    arguments: {
                        SECONDS: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 2
                        },
                        LANGUAGE: {
                            type: ArgumentType.STRING,
                            defaultValue: 'English (UK)',
                            menu: 'languagesMenu'
                        }
                    }
                },
                {
                    opcode: 'getSpeechRecognitionResult',
                    blockType: BlockType.REPORTER,
                    text: 'speech recognition result'
                },
                // {
                //     opcode: 'setNoiseThreshold',
                //     blockType: BlockType.COMMAND,
                //     text: 'set noise removal threshold to [PERCENT] %',
                //     arguments: {
                //         PERCENT: {
                //             type: ArgumentType.NUMBER,
                //             defaultValue: 30
                //         }
                //     }
                // },
                {
                    opcode: 'turnOnAutoClearing',
                    blockType: BlockType.COMMAND,
                    text: 'turn [STATE] auto clearing',
                    arguments: {
                        STATE: {
                            type: ArgumentType.STRING,
                            defaultValue: 'on',
                            menu: 'onOffMenu'
                        }
                    }
                },
                {
                    opcode: 'clearResultsAfterSeconds',
                    blockType: BlockType.COMMAND,
                    text: 'clear results after [SECONDS] seconds of speech',
                    arguments: {
                        SECONDS: {
                            type: ArgumentType.NUMBER,
                            defaultValue: Infinity
                        }
                    }
                },
                {
                    opcode: 'clearResultsAfterPause',
                    blockType: BlockType.COMMAND,
                    text: 'clear results after a pause of [SECONDS] seconds',
                    arguments: {
                        SECONDS: {
                            type: ArgumentType.NUMBER,
                            defaultValue: Infinity
                        }
                    }
                },
                {
                    opcode: 'setLanguage',
                    blockType: BlockType.COMMAND,
                    text: 'set language to [LANGUAGE]',
                    arguments: {
                        LANGUAGE: {
                            type: ArgumentType.STRING,
                            defaultValue: 'English (UK)',
                            menu: 'languagesMenu'
                        }
                    }
                },
                {
                    opcode: 'startListening',
                    blockType: BlockType.COMMAND,
                    text: 'start listening'
                },
                {
                    opcode: 'stopListening',
                    blockType: BlockType.COMMAND,
                    text: 'stop listening'
                },
                {
                    opcode: 'clearSpeechRecognitionResult',
                    blockType: BlockType.COMMAND,
                    text: 'clear speech recognition result'
                }
            ],
            menus: {
                languagesMenu: {
                    acceptReporters: true,
                    items: [
                        'Arabic', 'Catalan', 'Danish', 'German', 'English (UK)', 'English (US)', 'Spanish',
                        'Finnish', 'French', 'Gujarati', 'Hindi', 'Italian', 'Japanese', 'Korean', 'Marathi',
                        'Norwegian', 'Dutch', 'Polish', 'Portuguese', 'Russian', 'Swedish', 'Tamil', 'Telugu',
                        'Thai', 'Turkish', 'Chinese (Mandarin)'
                    ]
                },
                onOffMenu: {
                    acceptReporters: true,
                    items: ['on', 'off']
                }
            }
        };
    }

    recognizeSpeech(args, util) {
        const seconds = Math.max(1, Number(args.SECONDS) || 2);
        const language = args.LANGUAGE;
        const languageCodes = {
            'Arabic': 'ar-SA', 'Catalan': 'ca-ES', 'Danish': 'da-DK', 'German': 'de-DE',
            'English (UK)': 'en-GB', 'English (US)': 'en-US', 'Spanish': 'es-ES', 'Finnish': 'fi-FI',
            'French': 'fr-FR', 'Gujarati': 'gu-IN', 'Hindi': 'hi-IN', 'Italian': 'it-IT',
            'Japanese': 'ja-JP', 'Korean': 'ko-KR', 'Marathi': 'mr-IN', 'Norwegian': 'nb-NO',
            'Dutch': 'nl-NL', 'Polish': 'pl-PL', 'Portuguese': 'pt-PT', 'Russian': 'ru-RU',
            'Swedish': 'sv-SE', 'Tamil': 'ta-IN', 'Telugu': 'te-IN', 'Thai': 'th-TH',
            'Turkish': 'tr-TR', 'Chinese (Mandarin)': 'zh-CN'
        };

        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognition) {
            console.warn('Speech recognition not supported in this browser.');
            this.lastResult = 'Speech recognition not supported';
            return;
        }

        // Stop any previous recognition
        if (this.recognition) {
            try { this.recognition.stop(); } catch (e) {}
            this.recognition = null;
        }

        if (this.autoClear){
          this.lastResult = ''
        } else {
          this.lastResult += ' '
        }
        this.isListening = true;

        this.recognition = new SpeechRecognition();
        this.recognition.continuous = false;
        this.recognition.interimResults = false;
        this.recognition.lang = languageCodes[language] || 'en-US';

        this.recognition.onresult = (event) => {
          const transcript = event.results[0][0].transcript;
          this.lastResult += transcript;
          this.isListening = false;

          // 🔹 Clear after N seconds of speech (after result)
          if (this.clearAfterSeconds) {
            setTimeout(() => {
              this.clearSpeechRecognitionResult();
            }, this.clearAfterSeconds * 1000);
          }

          // 🔹 Reset pause timer each time new speech is recognized
          if (this.clearAfterPause) {
            if (this.pauseTimer) clearTimeout(this.pauseTimer);
            this.pauseTimer = setTimeout(() => {
              this.clearSpeechRecognitionResult();
            }, this.clearAfterPause * 1000);
          }
        };

        this.recognition.onerror = (event) => {
            this.lastResult = `Error: ${event.error}`;
            this.isListening = false;
            console.error(`Speech recognition error: ${event.error}`);
        };

        this.recognition.onend = () => {
            if (!this.lastResult) {
                this.lastResult = 'No speech detected';
            }
            this.isListening = false;
        };

        this.recognition.start();

        // Stop after the requested number of seconds
        setTimeout(() => {
            if (this.recognition && this.isListening) {
                try { this.recognition.stop(); } catch (e) {}
            }
        }, seconds * 1000);
    }

    getSpeechRecognitionResult(args, util) {
        // Return the last result, or a helpful message if still listening
        if (this.isListening) return 'Listening...';
        return this.lastResult || 'Null';
    }

    setNoiseThreshold(args) {
        this.noiseThreshold = args.PERCENT;
    }

    turnOnAutoClearing(args) {
        this.autoClear = args.STATE === 'on';
    }

    clearResultsAfterSeconds(args) {
      const seconds = Number(args.SECONDS);
      if (isFinite(seconds) && seconds > 0) {
        this.clearAfterSeconds = seconds;
      } else {
        this.clearAfterSeconds = null;
      }
    }

    clearResultsAfterPause(args) {
      const seconds = Number(args.SECONDS);
      if (isFinite(seconds) && seconds > 0) {
        this.clearAfterPause = seconds;

        // Start the pause timer right away
        if (this.pauseTimer) {
          clearTimeout(this.pauseTimer);
        }
        this.pauseTimer = setTimeout(() => {
          this.clearSpeechRecognitionResult();
        }, this.clearAfterPause * 1000);
      } else {
        this.clearAfterPause = null;
      }
    }

    setLanguage(args) {
      const languageCodes = {
            'Arabic': 'ar-SA', 'Catalan': 'ca-ES', 'Danish': 'da-DK', 'German': 'de-DE',
            'English (UK)': 'en-GB', 'English (US)': 'en-US', 'Spanish': 'es-ES', 'Finnish': 'fi-FI',
            'French': 'fr-FR', 'Gujarati': 'gu-IN', 'Hindi': 'hi-IN', 'Italian': 'it-IT',
            'Japanese': 'ja-JP', 'Korean': 'ko-KR', 'Marathi': 'mr-IN', 'Norwegian': 'nb-NO',
            'Dutch': 'nl-NL', 'Polish': 'pl-PL', 'Portuguese': 'pt-PT', 'Russian': 'ru-RU',
            'Swedish': 'sv-SE', 'Tamil': 'ta-IN', 'Telugu': 'te-IN', 'Thai': 'th-TH',
            'Turkish': 'tr-TR', 'Chinese (Mandarin)': 'zh-CN'
        };
        console.log("setting:", languageCodes[args.LANGUAGE], args.LANGUAGE)
        this.language = languageCodes[args.LANGUAGE]
        console.log(this.language)
    }

    resetSettings() {
        this.noiseThreshold = 30;
        this.autoClear = false;
        this.clearAfterSeconds = null;
        this.clearAfterPause = null;
    }

    startListening() {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SpeechRecognition) {
          console.error('Speech recognition not supported in this browser.');
          this.lastResult = 'Speech recognition not supported';
          return;
      }

      if (this.autoClear){
        this.lastResult = ''
      } else {
        this.lastResult += ' '
      }

      // Initialize recognition if not done yet
      if (!this.recognition) {
          this.recognition = new SpeechRecognition();
          this.recognition.continuous = false;
          this.recognition.interimResults = false;

          // Handlers
          this.recognition.onresult = (event) => {
            const transcript = event.results[0][0].transcript;
            this.lastResult += transcript;
            this.isListening = false;

            // 🔹 Clear after N seconds of speech (after result)
            if (this.clearAfterSeconds) {
              setTimeout(() => {
                this.clearSpeechRecognitionResult();
              }, this.clearAfterSeconds * 1000);
            }

            // 🔹 Reset pause timer each time new speech is recognized
            if (this.clearAfterPause) {
              if (this.pauseTimer) clearTimeout(this.pauseTimer);
              this.pauseTimer = setTimeout(() => {
                this.clearSpeechRecognitionResult();
              }, this.clearAfterPause * 1000);
            }
          };

          this.recognition.onerror = (event) => {
              this.lastResult = `Error: ${event.error}`;
              this.isListening = false;
              console.error(`Speech recognition error: ${event.error}`);
          };

          this.recognition.onend = () => {
              if (!this.lastResult) {
                  this.lastResult = 'No speech detected';
              }
              this.isListening = false;
          };
      }

      // ✅ Always update language before starting
      if (this.language) {
          this.recognition.lang = this.language;
      }

      // Start recognition if not already listening
      if (!this.isListening) {
          this.isListening = true;
          this.recognition.start();
      }
    }

    stopListening() {
        if (this.recognition && this.isListening) {
            this.recognition.stop();
            this.isListening = false;
        } else if (!this.recognition) {
            console.error('Recognition instance not found. Please initialize it first.');
        }
    }

    clearSpeechRecognitionResult() {
        this.clearRecognitionResult();
    }

    clearRecognitionResult() {
        this.lastResult = '';
        this.isListening = false;
    }
}

module.exports = Scratch3SpeechRecognitionExtension;
